import { Component, Input, OnChanges, SimpleChanges, OnDestroy, OnInit, EventEmitter, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { CustomerService } from '../../../api/customer.service';
import { UserProfileService } from '../../../api/user-profile.service';
import { RepresentativeTypes, TransactionTypes } from '../../../models/transactions-enum';
import { Countries } from '../../../utils/countries-enum';
import { DataObject } from '../../../interfaces/data-objects';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import uuid from '../../../utils/uuid';
import { FormService } from '../../../api/form.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'iv-bill-to-financials',
  templateUrl: './bill-to-financials.component.html',
  styleUrls: ['./bill-to-financials.component.scss']
})
export class BillToFinancialsComponent implements OnChanges, OnDestroy, OnInit {

  @Input() transaction: DataObject;
  @Input() userCountry: Countries;
  @Input() disabled = false;
  @Output() saveTransaction: EventEmitter<DataObject>;
  
  contact = [];
  company = '';
  repParty = '';
  address: any;

  uuid = uuid();
  notesForm: UntypedFormGroup;
  enableInCapacityAndCareOfNotes = false;

  constructor(
    private customerService: CustomerService,
    private userProfileService: UserProfileService,
    private fb: UntypedFormBuilder,
    private formService: FormService,
  ) {
    this.saveTransaction = new EventEmitter<DataObject>();
  }

  ngOnInit() {
    this.enableInCapacityAndCareOfNotes = [TransactionTypes.VALUATION, TransactionTypes.CONSULTATION, TransactionTypes.CONSULTING, TransactionTypes.LEASE, TransactionTypes.SALE].includes(this.transaction.transaction_type)
      && this.userCountry === Countries.Singapore;
    this.setupForm();
  }
 
  ngOnChanges(changes: SimpleChanges) {
    if (changes.transaction?.currentValue?.bill_to_party || changes.transaction?.currentValue?.represented_party) {
      const isThirdParty = this.isPacificIsolationEnable() ? this.transaction.bill_to_cep_reprole === 'thirdparty' : this.transaction.bill_to_third_party;
      if (isThirdParty && this.transaction.bill_to_party !== null) {
        this.customerService.getById(this.transaction.bill_to_party)
          .pipe(untilDestroyed(this))
          .subscribe((bill_to) => {
            this.contact = this.transaction.bill_to_name;
            this.company = this.getCompanyName(bill_to.results[0]);
            this.repParty = 'Third Party';
            // tslint:disable-next-line:max-line-length
            const _address = bill_to.results[0].addresses.addresses.filter(address => +address.address_seq_num === +this.transaction.bill_to_address);
            if (_address && _address[0]) {
              this.address = _address[0];
            }
          });
      } else if (this.transaction?.represented_party !== null) {
        this.customerService.getById(this.transaction.represented_party)
          .pipe(untilDestroyed(this))
          .subscribe((represented_party) => {
            this.contact = this.transaction.represented_contact_name;
            // Bill to CEP Rep Role
            if (this.transaction.bill_to_cep_reprole) {
              if (this.transaction.transaction_type === TransactionTypes.RESI) {
                this.repParty = 'Vendor';
              } else if (this.transaction.transaction_type === TransactionTypes.VALUATION) {
                this.repParty = 'Client';
              } else {
                this.repParty = `${this.transaction.bill_to_cep_reprole}`.charAt(0).toUpperCase() + `${this.transaction.bill_to_cep_reprole}`.slice(1);
              }
            } else {
              // Bill to Rep Role
              if ([TransactionTypes.CONSULTATION, TransactionTypes.CONSULTING].includes(this.transaction.transaction_type)) {
                this.repParty = 'Customer';
              } else {
                this.repParty = `${this.transaction.rep_role}`.charAt(0).toUpperCase() + `${this.transaction.rep_role}`.slice(1);
              }
            }
            
            if (represented_party?.results?.length > 0) {
              this.company = this.getCompanyName(represented_party.results[0]);
              // tslint:disable-next-line:max-line-length
              const _address = represented_party.results[0].addresses.addresses.filter(address => +address.address_seq_num === +this.transaction.represented_address);
              if (_address && _address[0]) {
                this.address = _address[0];
              }
            }
          });
      }
    }
  }

  setupForm() {
    this.notesForm = this.fb.group({
      care_of_notes: [
        { value: this.transaction?.care_of_notes || '', disabled: this.disabled },
      ],
      in_capacity_notes: [
        { value: this.transaction?.in_capacity_notes || '', disabled: this.disabled },
      ],
    });
    this.formService.addLoadedForm(this.notesForm, this.uuid);
    this.notesForm.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(1000),
        distinctUntilChanged()
      )
      .subscribe((changes: any) => {
        this.saveTransaction.emit(changes);
      });
  }

  isPacificIsolationEnable() {
    if (this.transaction.is_residential_business_line) {
      return false;
    }
    return this.userProfileService.isPacificIsolationEnable(this.transaction);
  }

  getCompanyName(customer: any): string {
    return this.customerService.getCustomerName(customer);
  }

  ngOnDestroy() { }
}


